/* eslint-disable @typescript-eslint/prefer-for-of */
import dayjs from "dayjs";
import { find, has } from "lodash";
import {
  isEventStartCloseToNow,
  type newCountryType,
  parseSearchData,
  type Translationtype,
} from "utils";
import * as Yup from "yup";
import { fetchAllCountries } from "../../../apps/dashboard/store/slices/tempDataStorageSlice";
import { type AppDispatch } from "../../../apps/dashboard/store/store";
import { fetchCountries } from "../services";

function stringRequired(message?: string) {
  return Yup.string().required(message || "Form.required");
}

function numberRequired(TypeErrorMessage?: string) {
  return Yup.number()
    .typeError(TypeErrorMessage || "Form.onlyNumberNotNull")
    .required("Form.required");
}

function isNumeric(str: string) {
  // Try to parse the string as a number
  const num = Number(str);
  // Check if the parsed number is not NaN and the input string is not empty
  return !isNaN(num) && str.trim() !== "";
}

function numberPositiveOrNull() {
  return Yup.number()
    .transform((_, val) => (isNumeric(val) ? Number(val) : null))
    .min(0)
    .max(9999999)
    .nullable();
  // checking self-equality works for NaN, transforming it to null
}

function numberOrNull(message?: string) {
  return Yup.number()
    .positive()
    .nullable()
    .typeError(message || "Form.numberOrNull")
    .required("Form.required");
}

function numberOrEmptyString() {
  return Yup.number()
    .transform((value, originalValue) => {
      return originalValue === "" ? -1 : Number(originalValue);
    })
    .notRequired();
}

function booleanRequired(message?: string) {
  return Yup.boolean().required(message || "Form.required");
}

function trueBooleanRequired(message?: string) {
  return Yup.boolean().oneOf([true], message || "Form.required");
}

function fileValidator(allowedTypes: string[]) {
  return Yup.mixed()
    .required("Form.fileSelectRequired")
    .test("type", "Form.wrongFileFormat", (value: any) => {
      return allowedTypes.includes(value && value[0]?.type);
    });
}

function notRequiredFileValidator(allowedTypes: string[]) {
  return Yup.mixed()
    .test("type", "Form.wrongFileFormat", (files: any) => {
      if (!files) return true;
      // Convert FileList or single file to array
      const fileArray = files instanceof FileList 
        ? Array.from(files) 
        : Array.isArray(files) 
          ? files 
          : [files];
      if (fileArray.length === 0) return true;
      return fileArray.every((file: any) => file && allowedTypes.includes(file.type));
    })
    .nullable();
}

function arrayMinValidator(minLength: number) {
  return Yup.mixed().test(
    "requiredMin",
    `Minimum ${minLength} elem megadása kötelező!`,
    (value: any) => {
      return value.length >= minLength;
    }
  );
}

function arrayMaxValidator(maxLength: number) {
  return Yup.mixed().test(
    "requiredMax",
    `Maximum ${maxLength} elem megadása lehetséges!`,
    (value: any) => {
      return value.length <= maxLength;
    }
  );
}

function arrayLengthValidator(minLength: number, maxLength: number) {
  return Yup.mixed()
    .test(
      "requiredMin",
      `Minimum ${minLength} elem megadása kötelező!`,
      (value: any) => {
        return value && value.length === minLength;
      }
    )
    .test(
      "requiredMax",
      `Maximum ${maxLength} elem megadása lehetséges!`,
      (value: any) => {
        return value && value.length === maxLength;
      }
    );
}

function stringArrayValidator(minLength?: number) {
  return Yup.mixed()
    .test(
      "requiredMin",
      `Minimum ${minLength} elem megadása kötelező!`,
      (value: any) => {
        return value.length >= (minLength || 0);
      }
    )
    .test(
      "childsCanNotBeEmpty",
      `Nem tartalmazhat üres mezőt. Kérem törölje vagy töltse ki az üres mezőt/mezőket! `,
      (value: any) => {
        for (let i = 0; i < value.length; i++) {
          if (value[i] === "") return false;
        }
        return true;
      }
    );
}

function dateValidator() {
  return Yup.mixed().test("required", "Form.required", (value: any) => {
    return value && value.startDate !== null && value.endDate !== null;
  });
}

function eventStartCloseToNow(t: Translationtype, isAdmin: boolean) {
  return Yup.mixed()
    .test("required", "Form.required", (value: any) => {
      return value;
    })
    .test(
      "eventStartCloseToNow",
      t("Form.eventStartCloseToNow"),
      (value: Date) => {
        if (!value) return true;
        if (isAdmin) {
          return true;
        } else if (isEventStartCloseToNow(value.toDateString())) {
          return false; // If the event start is close to now, validation fails
        }
        return true; // Otherwise, validation passes
      }
    );
}

function dateFromBeforeToValidator(startDateKey: string) {
  return Yup.mixed().test(
    "required",
    "Form.required",
    (value: any, context: Yup.TestContext) => {
      return (
        value &&
        value.startDate !== null &&
        value.endDate !== null &&
        dayjs(context.parent[startDateKey].startDate).isBefore(
          dayjs(value.endDate)
        )
      );
    }
  );
}

function dateWithContextValidator(contextKey: string) {
  return Yup.mixed().test(
    "required",
    "Form.required",
    (value: any, context: Yup.TestContext) => {
      if (context.parent && context.parent[contextKey]) {
        return true;
      }
      return value && value.startDate !== null && value.endDate !== null;
    }
  );
}

const passwordRules = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

function passwordValidator(t: Translationtype) {
  return Yup.string()
    .required(t("Form.required"))
    .test(
      "passwordTest",
      t("Users.newPasswordModal.passwordValidationMessage"),
      (value: string) => {
        return value && passwordRules.test(value);
      }
    );
}

function dateDurationValid(maxDuration: number, t: Translationtype) {
  return Yup.mixed()
    .test("required", "Form.required", (value: any) => {
      return value && value.startDate !== null && value.endDate !== null;
    })
    .test(
      "maxDuration",
      `${t("Form.eventMaxDuration")} ${maxDuration} ${t(
        "Form.eventMaxDurationDays"
      )}`,
      (value: any) => {
        // Calculate the duration between startDate and endDate in days
        const durationInDays = dayjs(value.endDate).diff(
          dayjs(value.startDate),
          "day"
        );
        // Return true if the duration is less than or equal to maxDuration days
        return durationInDays < maxDuration;
      }
    );
}

function stringOrObject() {
  return Yup.mixed().test(
    "isObjectOrString",
    "Form.objectOrString",
    (value: any) => {
      if (value === null || value === undefined) return false;
      return typeof value === "object" || typeof value === "string";
    }
  );
}

let cachedCountryList: newCountryType[] | null = null;

async function getCachedCountries() {
  if (!cachedCountryList) {
    cachedCountryList = await fetchCountries();
  }
  return cachedCountryList;
}

function countrySearchValidator(t: Translationtype, dispatch?: AppDispatch) {
  return Yup.mixed()
    .test("required", t("Form.required"), (value: any) => {
      return value && value.id !== null;
    })
    .test("requredCity", t("Form.requiredCity"), (value: any) => {
      return value && has(value, "context");
    })
    .test("shippingEnabled", t("Form.weDoNotShip"), async (value: any) => {
      const searchData = parseSearchData(value);
      const countryCode = searchData?.countryCode;

      let countryList;

      if (dispatch) {
        // use dispatch version
        try {
          countryList = await dispatch(fetchAllCountries()).unwrap();
        } catch (error) {
          return false;
        }
      } else {
        // fallback to cache
        countryList = await getCachedCountries();
      }

      const found = find(countryList, { code: countryCode });
      return value && found?.shippingEnabled;
    });
}

function emailValidator(t?: any) {
  return Yup.string()
    .trim()
    .email(t ? t("Form.wrongEmailFormat") : "Form.wrongEmailFormat")
    .required(t ? t("Form.required") : "Form.required");
}

function audioSettingsValidator() {
  return Yup.number().min(0).max(100).required("Form.required");
}

function selectionValidator() {
  return Yup.object()
    .shape({
      value: Yup.string(),
      label: Yup.string(),
    })
    .nullable()
    .required("Form.required");
}

function shippingAndReturnDateValidator(isAdmin: boolean, t: Translationtype) {
  return Yup.mixed()
    .nullable()
    .test(
      "shipping-return-date-logic",
      "",
      (value: dateType | null, context: Yup.TestContext) => {
        const { path, createError, parent } = context;
        const returnDate: dateType = parent["returnDay"];
        const hasValue = value && value.startDate;
        const hasReturnDate = returnDate && returnDate.startDate;
        const fillShippingLater: boolean = parent["fillShippingLater"];
        const fillReturnLater: boolean = parent["fillReturnLater"];
        if (!isAdmin) return true;
        if (fillShippingLater) return true;
        if (fillShippingLater && hasValue && !fillReturnLater) {
          return true;
        }

        const returnGivenMessage = t("Form.returnDateRequiredIfShippingGiven");
        const shippingGivenMessage = t(
          "Form.shippingDateRequiredIfReturnGiven"
        );
        const shippingBeforeReturnMessage = t("Form.shippingBeforeReturn");

        if (!hasValue && hasReturnDate) {
          return createError({
            path,
            message: shippingGivenMessage,
          });
        }

        if (hasValue && !hasReturnDate) {
          return createError({
            path,
            message: returnGivenMessage,
          });
        }

        if (hasValue && hasReturnDate) {
          if (value.startDate! >= returnDate.startDate!) {
            return createError({
              path,
              message: shippingBeforeReturnMessage,
            });
          }
        }
        return true;
      }
    );
}

function customVideoValidator(
  t: Translationtype,
  allowedTypes: string[],
  resolution: { width: number; height: number },
  fileSizeInMB: number
) {
  const resolutionErrorMessage = t("Form.maxResolution", {
    width: resolution.width || "",
    height: resolution.height || "",
  });
  const fileSizeErrorMessage = t("Form.maxFileSize", {
    size: fileSizeInMB || "",
  });

  return Yup.mixed()
    .required("Form.required")
    .test("type", "Form.wrongFileFormat", (value: any) => {
      return value[0] ? allowedTypes.includes(value && value[0]?.type) : true;
    })
    .test("fileSize", fileSizeErrorMessage, (file: any) =>
      file[0] ? file[0].size / 1000000 <= fileSizeInMB : false
    )
    .test("fileResolution", resolutionErrorMessage, (file: any) => {
      if (!file[0]) return false;
      return new Promise((resolve) => {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () => {
          window.URL.revokeObjectURL(video.src);
          const isValid =
            video.videoWidth <= resolution.width &&
            video.videoHeight <= resolution.height;
          resolve(isValid);
        };
        video.onerror = () => resolve(false);
        video.src = URL.createObjectURL(file[0]);
      });
    });
}

export type dateType = {
  startDate: string;
  endDate: string;
};

export const yupValidators = {
  stringRequired,
  numberRequired,
  booleanRequired,
  fileValidator,
  notRequiredFileValidator,
  arrayMinValidator,
  arrayMaxValidator,
  arrayLengthValidator,
  dateValidator,
  stringArrayValidator,
  emailValidator,
  numberOrNull,
  countrySearchValidator,
  stringOrObject,
  numberPositiveOrNull,
  dateDurationValid,
  selectionValidator,
  eventStartCloseToNow,
  customVideoValidator,
  dateFromBeforeToValidator,
  passwordValidator,
  trueBooleanRequired,
  dateWithContextValidator,
  shippingAndReturnDateValidator,
  numberOrEmptyString,
  audioSettingsValidator,
};
